// ============================================================
// Icons — kompakte SVG-Striche, Parameter: name, color, size
// ============================================================

import type { CSSProperties, ReactElement } from 'react';
import type { IconName } from './types';

export type { IconName };

interface IconProps {
  name: IconName;
  color?: string;
  size?: number;
  strokeWidth?: number;
  style?: CSSProperties;
  title?: string;
}

const PATHS: Record<IconName, ReactElement[]> = {
  grid: [
    <rect key="1" x={3} y={3} width={7} height={7} rx={1} />,
    <rect key="2" x={14} y={3} width={7} height={7} rx={1} />,
    <rect key="3" x={3} y={14} width={7} height={7} rx={1} />,
    <rect key="4" x={14} y={14} width={7} height={7} rx={1} />,
  ],
  line: [
    <path key="1" d="M3 17l5-6 4 3 6-8" />,
    <path key="2" d="M3 21h18" />,
  ],
  bars: [
    <rect key="1" x={4} y={11} width={4} height={9} rx={1} />,
    <rect key="2" x={10} y={6} width={4} height={14} rx={1} />,
    <rect key="3" x={16} y={14} width={4} height={6} rx={1} />,
  ],
  dots: [
    <circle key="1" cx={6} cy={6} r={1.6} />,
    <circle key="2" cx={12} cy={6} r={1.6} />,
    <circle key="3" cx={18} cy={6} r={1.6} />,
    <circle key="4" cx={6} cy={12} r={1.6} />,
    <circle key="5" cx={12} cy={12} r={1.6} />,
    <circle key="6" cx={6} cy={18} r={1.6} />,
    <circle key="7" cx={12} cy={18} r={1.6} />,
    <circle key="8" cx={18} cy={12} r={1.6} />,
    <circle key="9" cx={18} cy={18} r={1.6} />,
  ],
  list: [
    <line key="1" x1={8} y1={6} x2={20} y2={6} />,
    <line key="2" x1={8} y1={12} x2={20} y2={12} />,
    <line key="3" x1={8} y1={18} x2={20} y2={18} />,
    <line key="4" x1={4} y1={6} x2={4.01} y2={6} />,
    <line key="5" x1={4} y1={12} x2={4.01} y2={12} />,
    <line key="6" x1={4} y1={18} x2={4.01} y2={18} />,
  ],
  down: [
    <path key="1" d="M12 3v11" />,
    <path key="2" d="M7 10l5 5 5-5" />,
    <path key="3" d="M4 20h16" />,
  ],
  gear: [
    <circle key="1" cx={12} cy={12} r={3} />,
    <path key="2" d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M5 19l2-2" />,
  ],
  x: [<path key="1" d="M6 6l12 12M18 6L6 18" />],
  ext: [
    <path key="1" d="M14 4h6v6" />,
    <path key="2" d="M20 4l-9 9" />,
    <path key="3" d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />,
  ],
  chev: [<path key="1" d="M6 9l6 6 6-6" />],
  cal: [
    <rect key="1" x={3} y={5} width={18} height={16} rx={2} />,
    <path key="2" d="M3 9h18M8 3v4M16 3v4" />,
  ],
  check: [<path key="1" d="M4 12l5 5L20 6" />],
  warn: [
    <path key="1" d="M12 3l9 16H3z" />,
    <path key="2" d="M12 10v4M12 17v.01" />,
  ],
  lock: [
    <rect key="1" x={5} y={11} width={14} height={9} rx={2} />,
    <path key="2" d="M8 11V8a4 4 0 0 1 8 0v3" />,
  ],
};

export function Icon({ name, color = 'currentColor', size = 18, strokeWidth = 1.8, style, title }: IconProps) {
  const st: CSSProperties = {
    width: size,
    height: size,
    fill: 'none',
    stroke: color,
    strokeWidth,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    display: 'inline-block',
    flex: '0 0 auto',
    ...style,
  };
  return (
    <svg viewBox="0 0 24 24" style={st} aria-label={title ?? name} role={title ? 'img' : 'presentation'}>
      {PATHS[name]}
    </svg>
  );
}
